import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Gift, MessageCircle, Users } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

// Compact card for tenants: shows referral code + link + counts.
export default function ReferralCard({ className = "" }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  if (!data) return null;

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(data.link);
        toast.success("Referral link copied");
        return;
      }
      throw new Error("clipboard unavailable");
    } catch {
      // Fallback for insecure contexts / permissions issues
      try {
        const el = document.createElement("textarea");
        el.value = data.link;
        el.setAttribute("readonly", "");
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        toast.success("Referral link copied");
      } catch {
        toast.error("Copy failed — long-press the link to share");
      }
    }
  };

  return (
    <div className={`bg-gradient-to-br from-[#F0EBE1] to-[#D4DEC6]/40 border border-[#2B3A67]/15 rounded-lg p-5 ${className}`} data-testid="referral-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#2B3A67] font-semibold">
            <Gift size={13} /> Invite &amp; earn {data.reward_days} days free
          </div>
          <p className="text-sm text-[#1A1F2C] mt-2 leading-relaxed">
            Share your code. When a friend completes their first paid booking, <strong>both of you</strong> get {data.reward_days} days of LocaNest Access.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C6B7A]">Your code</div>
          <div className="font-display text-2xl text-[#C85A32]" data-testid="referral-code">{data.code}</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input
          readOnly
          value={data.link}
          onFocus={(e) => e.target.select()}
          className="flex-1 min-w-[220px] text-xs px-3 py-2 rounded border border-[#1A1F2C]/15 bg-[#FDFBF7] font-mono truncate"
          data-testid="referral-link"
        />
        <Button onClick={copy} size="sm" variant="outline" className="rounded-md" data-testid="referral-copy">
          <Copy size={13} className="mr-1" /> Copy link
        </Button>
        <Button
          asChild
          size="sm"
          className="rounded-md bg-[#25D366] hover:bg-[#1fb857] text-white"
          data-testid="referral-whatsapp"
        >
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`Yaar, LocaNest pe verified rentals mile — real owners, no brokerage-drama. Mera referral code ${data.code} use kar. Pehli paid booking ke baad hum dono ko 30 din free Access mil jayega 🎁 ${data.link}`)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle size={13} className="mr-1" /> WhatsApp
          </a>
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-center">
        <Stat label="Invited" v={data.referred_count} />
        <Stat label="Converted" v={data.converted_count} />
        <Stat label="Rewards earned" v={data.rewards_earned} />
      </div>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="bg-[#FDFBF7] border border-[#1A1F2C]/10 rounded-md py-2">
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#5C6B7A]">{label}</div>
      <div className="font-display text-xl text-[#2B3A67]">{v}</div>
    </div>
  );
}
